// Type for content property value structure
export type BylineContentPropertyValue = {
	title?: string;
	icon?: string;
	tooltip?: string;
};

// GraphQL query response types
type ContentPropertyNode = {
	id?: string;
	key?: string;
	version?: {
		number?: number;
	};
	value?: string;
};

type ContentNode = {
	id?: string;
	properties?: {
		nodes?: ContentPropertyNode[];
	};
};

type ContentPropertyQueryData =
	| {
			content?: {
				nodes?: ContentNode[];
			};
	  }
	| null
	| undefined;

// Utility function to check if a value is a non-empty string
const isNonEmptyString = (value: any): value is string => {
	return typeof value === 'string' && value.trim() !== '';
};

// Utility function to validate and set a byline content property value
const validatePropertyValue = (
	key: keyof BylineContentPropertyValue,
	value: any,
	validatedProperties: BylineContentPropertyValue,
) => {
	if (value !== undefined) {
		if (isNonEmptyString(value)) {
			validatedProperties[key] = value;
		} else {
			// eslint-disable-next-line no-console
			console.warn(`Byline content property ${key} must be a non-empty string, got:`, typeof value);
		}
	}
};

// Validation function for extracting property node from GraphQL query data
const validateContentPropertyQuery = (
	data: ContentPropertyQueryData,
	contentPropertyKey: string,
): { propertyNode: ContentPropertyNode; validContentPropertyKey: string | undefined } | null => {
	// Check if data exists and has the expected structure
	if (
		!data ||
		!data.content ||
		!data.content.nodes ||
		data.content.nodes.length === 0 ||
		!data.content.nodes[0].properties ||
		!data.content.nodes[0].properties.nodes ||
		data.content.nodes[0].properties.nodes.length === 0
	) {
		// eslint-disable-next-line no-console
		console.warn('Invalid byline content property query data');
		return null;
	}

	// Get all objects that match the contentPropertyKey
	const properties = data.content.nodes[0].properties.nodes;
	const contentKeyPattern = new RegExp(contentPropertyKey);
	const matchingObjects = properties.filter((property: ContentPropertyNode) =>
		contentKeyPattern.test(property.key ?? ''),
	);

	if (matchingObjects.length === 0) {
		// eslint-disable-next-line no-console
		console.warn('No property nodes matching content property key pattern found');
		return null;
	}

	if (matchingObjects.length > 1) {
		// eslint-disable-next-line no-console
		console.warn(
			'Multiple property nodes matching content property key pattern found. Please make sure your contentPropertyKey is unique, like a UUID.',
		);
		return null;
	}

	const propertyNode = matchingObjects[0];
	const validContentPropertyKey = propertyNode.key;

	if (!propertyNode.value) {
		// eslint-disable-next-line no-console
		console.warn('Byline content property data has no value');
		return null;
	}

	return { propertyNode, validContentPropertyKey };
};

// Common function to validate title, icon, and tooltip properties
const validateProperties = (
	propertyValue: any,
	validatedProperties: BylineContentPropertyValue,
) => {
	// validate each property
	validatePropertyValue('title', propertyValue.title, validatedProperties);
	validatePropertyValue('icon', propertyValue.icon, validatedProperties);
	validatePropertyValue('tooltip', propertyValue.tooltip, validatedProperties);
};

// Validation function for content property data
export const validateContentPropertyData = (
	data: ContentPropertyQueryData,
	defaultValues: { title: string; icon: string; tooltip: string; contentPropertyKey: string },
): { title: string; icon: string; tooltip: string; contentPropertyKey: string } => {
	try {
		// Validate and extract the query data
		const queryResult = validateContentPropertyQuery(data, defaultValues.contentPropertyKey);
		if (!queryResult) {
			return defaultValues;
		}

		const { propertyNode, validContentPropertyKey } = queryResult;

		// Parse JSON with error handling
		let propertyValue: BylineContentPropertyValue;
		try {
			propertyValue = JSON.parse(propertyNode.value ?? '') as BylineContentPropertyValue;
		} catch (jsonError) {
			// eslint-disable-next-line no-console
			console.error('Failed to parse byline content property JSON:', jsonError);
			return defaultValues;
		}

		// Validate the structure of the parsed value
		if (typeof propertyValue !== 'object' || propertyValue === null) {
			// eslint-disable-next-line no-console
			console.error('Byline content property value is not an object:', propertyValue);
			return defaultValues;
		}

		const validatedProperties = { ...defaultValues };

		// Validate each property using the byline content property value utility function
		validateProperties(propertyValue, validatedProperties);

		if (validContentPropertyKey && validContentPropertyKey !== '') {
			validatedProperties.contentPropertyKey = validContentPropertyKey;
		}

		return validatedProperties;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Unexpected error in content property validation:', error);
		return defaultValues;
	}
};

// Validation function for event data from FORGE_CONTENT_PROPERTY_UPDATED events
export const validateEventData = (
	eventData: any,
	defaultValues: { title: string; icon: string; tooltip: string },
): { title: string; icon: string; tooltip: string } => {
	try {
		// Check if eventData exists and has the expected structure
		if (!eventData || !eventData.propertyKey || !eventData.valueUpdate) {
			// eslint-disable-next-line no-console
			console.warn('Invalid FORGE_CONTENT_PROPERTY_UPDATED event data structure');
			return defaultValues;
		}

		const valueUpdate = eventData.valueUpdate;

		// Validate the structure of valueUpdate
		if (typeof valueUpdate !== 'object' || valueUpdate === null) {
			// eslint-disable-next-line no-console
			console.error('Event valueUpdate is not an object:', valueUpdate);
			return defaultValues;
		}

		const validatedProperties = { ...defaultValues };

		validateProperties(valueUpdate, validatedProperties);

		return validatedProperties;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Unexpected error in event data validation:', error);
		return defaultValues;
	}
};
